import { useEffect } from 'react'
import useIntersectionObserver from 'hooks/intersectionObserver'
import DataLayer, { Impression } from 'lib/dataLayer'
import AlgoliaEvents from 'lib/algoliaEvents'
import { getAlgoliaQueryIDFromLocalStorage } from 'lib/algolia/v2/helpers'
import { algoliaEventsApi } from 'apis/algolia'
import { convertFromCentsToDollars } from 'components/FormatPrice/helpers'
import { PolaroidSpecificPageProps } from './Polaroid'

type UsePolaroidEventsResponseType = {
  handleArtworkClick: (ev: React.SyntheticEvent<Record<string, any>>) => void
}

type UsePolaroidEventsParams = Pick<
  PolaroidSpecificPageProps,
  'algoliaIndice' | 'algoliaUserToken' | 'dataListId' | 'dataListName'
> & {
  imageRef: React.MutableRefObject<HTMLImageElement | null>
  artworkId: number
  artworkTitle: string
  artistName: string
  category: string
  listPrice: number
  sku: string
  dataPosition: number
  positionID: number
  userID: number
  originalStatus: string
  isPrintForDataLayerEvent?: boolean
  shouldSendAlgoliaEvents?: boolean
}

export const usePolaroidEvents = ({
  imageRef,
  artworkId,
  artworkTitle,
  artistName,
  category,
  listPrice,
  sku,
  dataPosition,
  algoliaIndice,
  algoliaUserToken,
  dataListId,
  dataListName,
  positionID,
  userID,
  originalStatus,
  isPrintForDataLayerEvent,
  shouldSendAlgoliaEvents = true,
}: UsePolaroidEventsParams): UsePolaroidEventsResponseType => {
  const { hasViewed } = useIntersectionObserver(imageRef)
  const algoliaQueryIdFromLocalStorage = getAlgoliaQueryIDFromLocalStorage()

  useEffect(() => {
    if (hasViewed) {
      // This is the new dataLayer event for GA4
      const impression: Impression = {
        // Strip trailing `s` - Paintings => Painting
        // Change Mixed Media => Mixedmedia
        item_category: category.replace(/s$/, '').replace(/\sM/, 'm'),
        item_id: sku || '',
        item_art_id: artworkId || '',
        item_name: artworkTitle || '',
        item_brand: artistName || '',
        item_variant: isPrintForDataLayerEvent ? 'open' : 'original',
        index: dataPosition,
        affiliation: 'Online Store',
        price: convertFromCentsToDollars(listPrice) || 0,
        quantity: 1,
        original_availability: originalStatus,
      }
      DataLayer.addImpressionEvent(impression, dataListId, dataListName)

      if (shouldSendAlgoliaEvents) {
        AlgoliaEvents.addImpressionEvent(
          artworkId,
          algoliaIndice,
          algoliaUserToken,
          algoliaQueryIdFromLocalStorage,
          userID
        )
      }
    }
  }, [hasViewed])

  const handleArtworkClick = (ev: React.SyntheticEvent<Record<string, any>>) => {
    const { id, position, list, name } = ev.currentTarget.dataset
    // Delete this dataLayer event in July as part of universal analytics sunset
    id &&
      window.dataLayer.push({
        event: 'productClick',
        ecommerce: {
          click: {
            actionField: {
              list, // feature-results
            },
            products: [
              {
                id, // Product sku
                position, // Location in the results
                name, // artwork title
              },
            ],
          },
        },
      })
    // This is the new dataLayer event for GA4
    // Clear the previous ecommerce object.
    window.dataLayer.push({
      ecommerce: null,
    })
    id &&
      window.dataLayer.push({
        event: 'select_item',
        ecommerce: {
          item_list_id: dataListId,
          item_list_name: dataListName,
          items: [
            {
              item_category: category.replace(/s$/, '').replace(/\sM/, 'm'),
              item_id: sku || '',
              item_art_id: artworkId || '',
              item_name: artworkTitle || '',
              item_brand: `${artistName}` || '',
              item_variant: isPrintForDataLayerEvent ? 'open' : 'original',
              index: positionID,
              item_list_id: dataListId,
              item_list_name: dataListName,
              affiliation: 'Online Store',
              price: convertFromCentsToDollars(listPrice) || 0,
              quantity: 1,
            },
          ],
        },
      })
    // Algolia event
    if (shouldSendAlgoliaEvents && id) {
      algoliaEventsApi({
        events: [
          {
            eventName: 'select_item',
            eventType: 'click',
            index: algoliaIndice,
            userToken: algoliaUserToken,
            authenticatedUserToken: String(userID) || '',
            objectIDs: [String(artworkId)],
            positions: [positionID],
            ...(algoliaQueryIdFromLocalStorage && {
              queryID: algoliaQueryIdFromLocalStorage,
            }), // pass queryID only if its present, otherwise do not pass
          },
        ],
      })
    }
  }

  return { handleArtworkClick }
}
